import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Pencil, Layers } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function HoldingsTable({ holdings, assetClasses, currency, onPriceUpdated }) {
  const [editingHolding, setEditingHolding] = useState(null);
  const [priceInput, setPriceInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [lotsHolding, setLotsHolding] = useState(null);
  const [lotsData, setLotsData] = useState(null);
  const [lotsLoading, setLotsLoading] = useState(false);

  const acMap = Object.fromEntries(assetClasses.map(a => [a.asset_class_id, a]));
  const fmt = (n) => `${currency} ${n.toFixed(2)}`;

  const openPriceEditor = (h) => {
    setEditingHolding(h);
    setPriceInput(h.current_price.toString());
  };

  const handleSavePrice = async () => {
    const val = parseFloat(priceInput);
    if (!(val > 0)) {
      toast.error('Price must be greater than 0');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API}/investments/holdings/${editingHolding.holding_id}/price`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ current_price: val })
      });
      if (res.ok) {
        toast.success('Price updated');
        setEditingHolding(null);
        onPriceUpdated();
      } else {
        toast.error('Failed to update price');
      }
    } catch {
      toast.error('Failed to update price');
    } finally {
      setSaving(false);
    }
  };

  const getPnLClass = (val) => (val >= 0 ? 'text-success' : 'text-destructive');

  const openLotsDialog = async (h) => {
    setLotsHolding(h);
    setLotsLoading(true);
    setLotsData(null);
    try {
      const res = await fetch(`${API}/investments/holdings/${h.holding_id}/lots`, {
        credentials: 'include'
      });
      if (res.ok) {
        setLotsData(await res.json());
      } else {
        toast.error('Failed to load lots');
      }
    } catch {
      toast.error('Failed to load lots');
    } finally {
      setLotsLoading(false);
    }
  };

  return (
    <Card data-testid="holdings-table-card" className="border border-border mb-8">
      <CardHeader>
        <CardTitle className="text-xl sm:text-2xl tracking-tight font-semibold">
          Holdings
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0 sm:p-6 sm:pt-0">
        {holdings.length === 0 ? (
          <div className="p-6 text-center py-12 text-muted-foreground text-sm">
            No holdings yet. Add a Buy transaction to start tracking.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-border">
                <tr className="text-left text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">
                  <th className="py-3 px-4">Symbol</th>
                  <th className="py-3 px-4">Class</th>
                  <th className="py-3 px-4 text-right">Qty</th>
                  <th className="py-3 px-4 text-right">Avg Price</th>
                  <th className="py-3 px-4 text-right">Current Price</th>
                  <th className="py-3 px-4 text-right">Market Value</th>
                  <th className="py-3 px-4 text-right">Unrealized P&L</th>
                  <th className="py-3 px-4 text-right">Realized P&L</th>
                  <th className="py-3 px-4 text-right">Lots</th>
                </tr>
              </thead>
              <tbody>
                {holdings.map((h) => {
                  const ac = acMap[h.asset_class_id];
                  return (
                    <tr
                      key={h.holding_id}
                      data-testid={`holding-${h.holding_id}`}
                      className="border-b border-border transition-colors duration-200 hover:bg-slate-50"
                    >
                      <td className="py-3 px-4 font-semibold">{h.symbol}</td>
                      <td className="py-3 px-4">
                        <Badge variant="outline" className="text-xs font-normal">
                          {ac?.name || h.asset_class_id}
                        </Badge>
                      </td>
                      <td className="py-3 px-4 text-right tabular-nums">{h.total_quantity}</td>
                      <td className="py-3 px-4 text-right tabular-nums">{fmt(h.avg_buy_price)}</td>
                      <td className="py-3 px-4 text-right tabular-nums">
                        <button
                          data-testid={`edit-price-${h.holding_id}`}
                          onClick={() => openPriceEditor(h)}
                          className="inline-flex items-center gap-1 hover:text-accent transition-colors"
                          title="Update price"
                        >
                          {fmt(h.current_price)}
                          <Pencil className="h-3 w-3 opacity-60" strokeWidth={1.5} />
                        </button>
                      </td>
                      <td className="py-3 px-4 text-right tabular-nums font-semibold">{fmt(h.market_value)}</td>
                      <td className={`py-3 px-4 text-right tabular-nums font-semibold ${getPnLClass(h.unrealized_pnl)}`}>
                        {h.unrealized_pnl >= 0 ? '+' : ''}{fmt(h.unrealized_pnl)}
                      </td>
                      <td className={`py-3 px-4 text-right tabular-nums font-semibold ${getPnLClass(h.realized_pnl)}`}>
                        {h.realized_pnl >= 0 ? '+' : ''}{fmt(h.realized_pnl)}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <button
                          data-testid={`view-lots-${h.holding_id}`}
                          onClick={() => openLotsDialog(h)}
                          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-accent transition-colors"
                          title="View FIFO tax lots"
                        >
                          <Layers className="h-3.5 w-3.5" strokeWidth={1.5} />
                          {h.open_lots_count ?? '—'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>

      <Dialog open={!!editingHolding} onOpenChange={(o) => !o && setEditingHolding(null)}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Update Price</DialogTitle>
            <DialogDescription>
              Set the current market price for {editingHolding?.symbol} in {currency}.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="price-input">Current Price ({currency})</Label>
            <Input
              data-testid="price-input"
              id="price-input"
              type="number"
              step="0.01"
              min="0.01"
              value={priceInput}
              onChange={(e) => setPriceInput(e.target.value)}
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingHolding(null)} disabled={saving}>
              Cancel
            </Button>
            <Button data-testid="save-price-button" onClick={handleSavePrice} disabled={saving}>
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!lotsHolding} onOpenChange={(o) => { if (!o) { setLotsHolding(null); setLotsData(null); } }}>
        <DialogContent data-testid="lots-dialog" className="sm:max-w-[640px]">
          <DialogHeader>
            <DialogTitle>FIFO Open Lots — {lotsHolding?.symbol}</DialogTitle>
            <DialogDescription>
              Tax-lot view: each buy creates an immutable lot. Sells consume the oldest lot first. Cost basis includes Buy-side charges.
            </DialogDescription>
          </DialogHeader>
          {lotsLoading ? (
            <div className="py-8 text-center text-sm text-muted-foreground">Loading lots…</div>
          ) : lotsData && lotsData.lots && lotsData.lots.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-border">
                  <tr className="text-left text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">
                    <th className="py-2 px-3">Acquired</th>
                    <th className="py-2 px-3 text-right">Qty Remaining</th>
                    <th className="py-2 px-3 text-right">Cost / Share</th>
                    <th className="py-2 px-3 text-right">Cost Basis</th>
                    <th className="py-2 px-3 text-right">Market Value</th>
                    <th className="py-2 px-3 text-right">Unrealized P&L</th>
                  </tr>
                </thead>
                <tbody>
                  {lotsData.lots.map((l) => (
                    <tr key={l.lot_id} data-testid={`lot-${l.lot_id}`} className="border-b border-border">
                      <td className="py-2 px-3 tabular-nums">{l.acquired_at}</td>
                      <td className="py-2 px-3 text-right tabular-nums">{l.qty_remaining}</td>
                      <td className="py-2 px-3 text-right tabular-nums">{fmt(l.cost_per_share)}</td>
                      <td className="py-2 px-3 text-right tabular-nums">{fmt(l.cost_basis)}</td>
                      <td className="py-2 px-3 text-right tabular-nums">{fmt(l.market_value)}</td>
                      <td className={`py-2 px-3 text-right tabular-nums font-semibold ${getPnLClass(l.unrealized_pnl)}`}>
                        {l.unrealized_pnl >= 0 ? '+' : ''}{fmt(l.unrealized_pnl)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="py-8 text-center text-sm text-muted-foreground">
              No open lots — all shares have been sold.
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}

export default HoldingsTable;
